/**
 * BusinessContextBanner — top-of-page summary of the AI-generated
 * business context for an enterprise connector.
 *
 * Sits ABOVE the per-table list in ``SchemaViewer`` so users see a
 * "what is this DB/file about" overview before they drill into a
 * single table. Reads from the EXISTING ``useProfilingStatus`` query
 * (the BE piggybacks ``business_context`` onto the profiling
 * snapshot) — no parallel TanStack query — and mutates through the
 * thin facade in ``api/businessContextApi.ts``.
 *
 * Four display modes:
 *   1. Profiling running              → render NOTHING (the parent
 *                                       already shows the sync
 *                                       indicator; a second banner
 *                                       would be noise).
 *   2. Has text, view mode (default)  → ~3-4 line truncated preview
 *                                       + "Mostra tutto" toggle +
 *                                       pencil + kebab.
 *   3. Has text, edit mode            → textarea + Salva / Annulla.
 *   4. No text, profiling complete    → empty state + "Genera con AI"
 *                                       CTA.
 *
 * On save: optimistic ``setQueryData`` against
 * ``enterpriseProfilingKey(connId)`` THEN invalidate, so the textarea
 * flips back to view mode immediately and the BE truth reconciles in
 * the background. On regenerate: invalidate + bump polling — the
 * existing 2s ``useProfilingStatus`` interval picks the new text up
 * automatically once the BE flips ``is_profiling`` back to false.
 */

import { Trans, useLingui } from "@lingui/react/macro";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, MoreHorizontal, Pencil, Sparkles, Wand2 } from "lucide-react";
import * as React from "react";

import type { ProfilingStatusResponse } from "@ando/core/api";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  cn,
  toast,
} from "@ando/ui";

import {
  regenerateBusinessContext,
  updateBusinessContext,
} from "../../api/businessContextApi";
import { enterpriseProfilingKey } from "../../hooks/enterpriseKeys";
import { useProfilingStatus } from "../../hooks/useProfilingStatus";
import { InlineErrorBanner } from "../InlineErrorBanner";

export interface BusinessContextBannerProps {
  readonly connId: string;
}

// Approx 3-4 lines of text at the banner width. Empirically tuned for
// the cream surface (px-5 py-4) at standard desktop widths.
const COLLAPSED_CHAR_LIMIT = 280;

// Treat any status that is NOT actively running as "settled". The
// banner only surfaces an empty-state CTA once we KNOW the BE has
// finished and produced nothing — otherwise we'd flash a CTA on
// initial mount for fresh connectors mid-discovery.
function isProfilingDone(status: ProfilingStatusResponse | undefined): boolean {
  if (!status) return false;
  return (
    !status.is_discovering &&
    !status.is_profiling &&
    (status.status === "complete" || status.status === "no_schemas")
  );
}

export const BusinessContextBanner: React.FC<BusinessContextBannerProps> = ({
  connId,
}) => {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const profilingQuery = useProfilingStatus(connId);
  const status = profilingQuery.data;

  const serverText = (status?.business_context ?? "").trim();
  const hasText = serverText.length > 0;
  const isRunning = Boolean(
    status?.is_discovering ||
      status?.is_profiling ||
      status?.status === "pending" ||
      status?.status === "in_progress",
  );
  const isDone = isProfilingDone(status);

  // ── Local UI state ──────────────────────────────────────────────────
  const [isEditing, setIsEditing] = React.useState(false);
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [draft, setDraft] = React.useState(serverText);
  const [error, setError] = React.useState<string | null>(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const [isRegenerating, setIsRegenerating] = React.useState(false);

  // Whenever the server snapshot changes (and we are NOT mid-edit), pull
  // the new text into the draft so opening the editor starts from the
  // current BE truth.
  const lastSyncedRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (isEditing) return;
    if (lastSyncedRef.current !== serverText) {
      lastSyncedRef.current = serverText;
      setDraft(serverText);
    }
  }, [serverText, isEditing]);

  const needsTruncation = serverText.length > COLLAPSED_CHAR_LIMIT;
  const displayText =
    needsTruncation && !isExpanded
      ? `${serverText.slice(0, COLLAPSED_CHAR_LIMIT).trimEnd()}…`
      : serverText;

  // ── Handlers ────────────────────────────────────────────────────────
  const handleStartEdit = React.useCallback(() => {
    setDraft(serverText);
    setError(null);
    setIsEditing(true);
  }, [serverText]);

  const handleCancel = React.useCallback(() => {
    setDraft(serverText);
    setError(null);
    setIsEditing(false);
  }, [serverText]);

  const handleSave = React.useCallback(async () => {
    setError(null);
    setIsSaving(true);

    // Optimistic patch: flip the cached profiling snapshot to the new
    // text so the banner re-renders in view mode immediately.
    const queryKey = enterpriseProfilingKey(connId);
    const previous = queryClient.getQueryData<ProfilingStatusResponse>(queryKey);
    if (previous) {
      queryClient.setQueryData<ProfilingStatusResponse>(queryKey, {
        ...previous,
        business_context: draft,
      });
    }

    try {
      await updateBusinessContext(connId, { business_context: draft });
      setIsEditing(false);
      void queryClient.invalidateQueries({ queryKey });
    } catch (err) {
      // Roll the optimistic patch back so the textarea preserves the
      // unsaved buffer for retry.
      if (previous) {
        queryClient.setQueryData<ProfilingStatusResponse>(queryKey, previous);
      }
      setError(err instanceof Error ? err.message : t`Salvataggio non riuscito.`);
    } finally {
      setIsSaving(false);
    }
  }, [connId, draft, queryClient, t]);

  const handleRegenerate = React.useCallback(async () => {
    setError(null);
    setIsRegenerating(true);
    toast(t`Stiamo rigenerando il contesto con l'AI…`);
    try {
      await regenerateBusinessContext(connId);
      // The BE starts the LLM run in the background; the existing 2s
      // ``useProfilingStatus`` poller picks the new text up once
      // ``is_profiling`` flips back to false. Kick a single refetch
      // here so the polling loop activates without waiting for the
      // staleTime window.
      void queryClient.invalidateQueries({
        queryKey: enterpriseProfilingKey(connId),
      });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t`Rigenerazione non riuscita.`,
      );
    } finally {
      setIsRegenerating(false);
    }
  }, [connId, queryClient, t]);

  // ── Render ──────────────────────────────────────────────────────────

  // Mode 1 — profiling still running. Suppress entirely: the parent
  // SchemaViewer already renders a sync indicator; a second banner
  // here would be redundant.
  if (isRunning) {
    return null;
  }

  // Mode 4 — profiling done, no text yet. Show the generate CTA.
  if (!hasText && isDone) {
    return (
      <section
        aria-label={t`Contesto di business`}
        className="flex flex-col gap-2 rounded-xl border border-hairline bg-cream/40 px-5 py-4"
      >
        <header className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-display font-semibold text-[13.5px] text-ink-1">
              <Trans>Contesto di business</Trans>
            </h3>
            <p className="mt-0.5 text-[11.5px] text-ink-3">
              <Trans>
                Riassumi cosa rappresenta questa connessione perché l'agente
                possa interpretarla correttamente.
              </Trans>
            </p>
          </div>
        </header>
        <InlineErrorBanner message={error} />
        <div className="flex justify-end">
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={handleRegenerate}
            disabled={isRegenerating}
          >
            {isRegenerating ? (
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
            ) : (
              <Wand2 className="h-3 w-3" aria-hidden />
            )}
            <Trans>Genera con AI</Trans>
          </Button>
        </div>
      </section>
    );
  }

  // No text yet AND profiling not yet known to be done — hold off on
  // rendering anything to avoid a CTA flash on initial mount.
  if (!hasText) {
    return null;
  }

  // Modes 2 + 3 — has text.
  return (
    <section
      aria-label={t`Contesto di business`}
      className="flex flex-col gap-3 rounded-xl border border-hairline bg-cream/40 px-5 py-4"
    >
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display font-semibold text-[13.5px] text-ink-1">
            <Trans>Contesto di business</Trans>
          </h3>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded-full bg-accent/10 px-1.5 py-0.5 font-medium text-[10px] text-accent">
            <Sparkles className="h-3 w-3" aria-hidden />
            <Trans>AI generato</Trans>
          </span>
          {!isEditing ? (
            <>
              <button
                type="button"
                onClick={handleStartEdit}
                aria-label={t`Modifica contesto`}
                className="rounded-md p-1 text-ink-3 transition-colors hover:bg-black/[0.04] hover:text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange"
              >
                <Pencil className="h-3.5 w-3.5" aria-hidden />
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label={t`Altre azioni`}
                    className="rounded-md p-1 text-ink-3 transition-colors hover:bg-black/[0.04] hover:text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange"
                  >
                    <MoreHorizontal className="h-3.5 w-3.5" aria-hidden />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onSelect={() => {
                      void handleRegenerate();
                    }}
                    disabled={isRegenerating}
                  >
                    <Sparkles className="mr-2 h-3.5 w-3.5" aria-hidden />
                    <Trans>Rigenera con AI</Trans>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : null}
        </div>
      </header>

      {isEditing ? (
        <BusinessContextEditMode
          draft={draft}
          onDraftChange={setDraft}
          isSaving={isSaving}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      ) : (
        <BusinessContextViewMode
          text={displayText}
          isTruncated={needsTruncation}
          isExpanded={isExpanded}
          onToggleExpand={() => setIsExpanded((prev) => !prev)}
        />
      )}

      <InlineErrorBanner message={error} />
    </section>
  );
};
BusinessContextBanner.displayName = "BusinessContextBanner";

// ──────────────────────────────────────────────────────────────────────
// Sub-components
// ──────────────────────────────────────────────────────────────────────

interface BusinessContextViewModeProps {
  readonly text: string;
  readonly isTruncated: boolean;
  readonly isExpanded: boolean;
  readonly onToggleExpand: () => void;
}

const BusinessContextViewMode: React.FC<BusinessContextViewModeProps> = ({
  text,
  isTruncated,
  isExpanded,
  onToggleExpand,
}) => (
  <div className="flex flex-col gap-1.5">
    <div
      className={cn(
        "prose prose-sm max-w-none whitespace-pre-wrap text-[12.5px] text-ink-1 leading-relaxed",
      )}
    >
      {text}
    </div>
    {isTruncated ? (
      <button
        type="button"
        onClick={onToggleExpand}
        className="self-start rounded font-medium text-[11.5px] text-accent transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange"
      >
        {isExpanded ? <Trans>Mostra meno</Trans> : <Trans>Mostra tutto</Trans>}
      </button>
    ) : null}
  </div>
);
BusinessContextViewMode.displayName = "BusinessContextViewMode";

interface BusinessContextEditModeProps {
  readonly draft: string;
  readonly onDraftChange: (value: string) => void;
  readonly isSaving: boolean;
  readonly onSave: () => void;
  readonly onCancel: () => void;
}

const BusinessContextEditMode: React.FC<BusinessContextEditModeProps> = ({
  draft,
  onDraftChange,
  isSaving,
  onSave,
  onCancel,
}) => {
  const textareaId = React.useId();
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={textareaId} className="sr-only">
        <Trans>Testo del contesto di business</Trans>
      </label>
      <textarea
        id={textareaId}
        value={draft}
        onChange={(e) => onDraftChange(e.target.value)}
        rows={8}
        disabled={isSaving}
        className="w-full resize-y rounded-md border border-hairline bg-white/60 px-3 py-2 text-[12.5px] text-ink-1 leading-relaxed placeholder:text-ink-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange disabled:bg-ink-3/[0.04]"
      />
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={isSaving}
        >
          <Trans>Annulla</Trans>
        </Button>
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={onSave}
          disabled={isSaving}
        >
          {isSaving ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : null}
          <Trans>Salva</Trans>
        </Button>
      </div>
    </div>
  );
};
BusinessContextEditMode.displayName = "BusinessContextEditMode";
